import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const MethodologySection = () => {
  const [activeApproach, setActiveApproach] = useState('income');

  const approaches = [
    {
      id: 'income',
      name: 'Income Approach',
      icon: 'TrendingUp',
      description: 'Values business based on its ability to generate future economic benefits',
      methods: [
        'Discounted Cash Flow (DCF)',
        'Capitalization of Earnings',
        'Dividend Discount Model'
      ],
      bestFor: 'Profitable businesses with predictable cash flows',
      timeline: '3-4 weeks',
      complexity: 'High'
    },
    {
      id: 'market',
      name: 'Market Approach',
      icon: 'BarChart3',
      description: 'Compares business to similar companies or recent transactions',
      methods: [
        'Guideline Public Company',
        'Guideline Transaction Method',
        'Market Multiples Analysis'
      ],
      bestFor: 'Companies with comparable market data available',
      timeline: '2-4 weeks',
      complexity: 'Medium'
    },
    {
      id: 'asset',
      name: 'Asset Approach',
      icon: 'Building',
      description: 'Values business based on fair value of assets minus liabilities',
      methods: [
        'Adjusted Net Asset Method',
        'Liquidation Value Method',
        'Replacement Cost Method'
      ],
      bestFor: 'Asset-heavy businesses or holding companies',
      timeline: '2-4 weeks',
      complexity: 'Low'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Engagement Planning',
      description: 'Define scope, purpose, and valuation date',
      duration: '1-2 days',
      deliverables: ['Engagement letter', 'Data request list']
    },
    {
      step: '02',
      title: 'Information Gathering',
      description: 'Collect financial data and business information',
      duration: '3-5 days',
      deliverables: ['Management interviews', 'Document review']
    },
    {
      step: '03',
      title: 'Analysis & Valuation',
      description: 'Apply appropriate valuation methodologies',
      duration: '5-10 days',
      deliverables: ['Financial analysis', 'Valuation calculations']
    },
    {
      step: '04',
      title: 'Report Preparation',
      description: 'Compile comprehensive valuation report',
      duration: '3-5 days',
      deliverables: ['Draft report', 'Final report']
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-primary mb-6">
            Our Valuation Methodology
          </h2>
          <p className="text-xl text-text-secondary leading-relaxed">
          We follow NACVA, AICPA and other recognized valuation standards, applying disciplined financial analysis to deliver independent and defensible valuation opinions.
          </p>
        </div>

        {/* Valuation Approaches */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Three Fundamental Approaches</h3>
          
          {/* Approach Tabs */}
          <div className="flex flex-col lg:flex-row gap-4 mb-8">
            {approaches?.map((approach) => (
              <button
                key={approach?.id}
                onClick={() => setActiveApproach(approach?.id)}
                className={`flex-1 p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                  activeApproach === approach?.id
                    ? 'border-accent bg-accent/5 shadow-brand'
                    : 'border-border bg-white hover:border-accent/50 hover:bg-accent/2'
                }`}
              >
                <div className="flex items-center space-x-4 mb-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    activeApproach === approach?.id ? 'bg-accent text-white' : 'bg-muted text-text-secondary'
                  }`}>
                    <Icon name={approach?.icon} size={24} />
                  </div>
                  <h4 className="text-lg font-semibold text-primary">{approach?.name}</h4>
                </div>
                <p className="text-sm text-text-secondary">{approach?.description}</p>
              </button>
            ))}
          </div>

          {/* Active Approach Details */}
          <div className="bg-muted rounded-2xl p-8">
            {approaches?.map((approach) => (
              activeApproach === approach?.id && (
                <div key={approach?.id} className="grid lg:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <h4 className="text-xl font-semibold text-primary mb-4">Methods Used</h4>
                      <ul className="space-y-3">
                        {approach?.methods?.map((method, index) => (
                          <li key={index} className="flex items-center space-x-3">
                            <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
                            <span className="text-text-primary">{method}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-semibold text-primary mb-2">Best Suited For</h4>
                      <p className="text-text-secondary">{approach?.bestFor}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="Clock" size={16} className="text-accent" />
                          <span className="text-sm font-medium text-text-secondary">Timeline</span>
                        </div>
                        <p className="font-semibold text-primary">{approach?.timeline}</p>
                      </div>
                      <div className="bg-white p-4 rounded-lg">
                        <div className="flex items-center space-x-2 mb-2">
                          <Icon name="BarChart" size={16} className="text-accent" />
                          <span className="text-sm font-medium text-text-secondary">Complexity</span>
                        </div>
                        <p className="font-semibold text-primary">{approach?.complexity}</p>
                      </div>
                    </div>
                    
                    <div className="bg-white p-6 rounded-lg">
                      <h5 className="font-semibold text-primary mb-3">Key Considerations</h5>
                      <ul className="text-sm text-text-secondary space-y-2">
                        <li>• Data availability and quality requirements</li>
                        <li>• Market conditions and comparability factors</li>
                        <li>• Business-specific risk adjustments</li>
                        <li>• Regulatory and compliance considerations</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>

        {/* Process Timeline */}
        <div>
          <h3 className="text-2xl font-bold text-primary mb-8 text-center">Our Valuation Process</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps?.map((step, index) => (
              <div key={index} className="relative">
                <div className="bg-white border border-border rounded-xl p-6 hover:shadow-brand transition-shadow duration-300">
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-12 h-12 bg-accent text-white rounded-lg flex items-center justify-center font-bold">
                      {step?.step}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-primary">{step?.title}</h4>
                      <p className="text-sm text-accent">{step?.duration}</p>
                    </div>
                  </div>
                  
                  <p className="text-text-secondary mb-4">{step?.description}</p>
                  
                  <div>
                    <h5 className="text-sm font-medium text-primary mb-2">Key Deliverables:</h5>
                    <ul className="text-sm text-text-secondary space-y-1">
                      {step?.deliverables?.map((deliverable, idx) => (
                        <li key={idx} className="flex items-center space-x-2">
                          <Icon name="Dot" size={12} className="text-accent" />
                          <span>{deliverable}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                
                {/* Connector Line */}
                {index < processSteps?.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-border transform -translate-y-1/2"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MethodologySection;